import { EventObject } from 'xstate';
import { Application, ApplyInput, CartValidation, FormTypes, HardDecline, SoftDecline, SubmitApplicationError } from '../../gql/graphql';
import { createForbiddenErrorMessage, createUnauthorizedErrorMessage } from '../../helpers/security/security';
import applicationFormService from '../../services/applicationForm.service';
import { NetworkError } from '../../state-machine/actions/genericActions';
import { ApplyRequestMachineContext } from '../../state-machine/types/shared/context/applyMachineContext';
import { CustomerFlowType } from '../../constants/customer-flow-type';
import { nullifyLine2 } from '../../helpers/validators/address.helper';
import { identify } from '../../state-machine/actions/segment';
import { isLongForm } from '../../helpers/converters/isLongForm';
import { LocalStorageKeys } from '../../constants';
import { buildAnalytics } from '../../helpers/analytics/buildAnalytics';

export type ApplyResult = {
  application?: Application;
  customerFlowType?: CustomerFlowType;
  applyErrors?: SubmitApplicationError[];
  applyResolverErrors?: any[]; // an experiment to see if errors ever used
  hardDeclineError?: HardDecline;
  softDeclineError?: SoftDecline;
  cartValidationError?: CartValidation;
};

// TODO: remove
export const APPLY_EVENT = 'APPLY_EVENT' as const;
export interface ApplyRequestEvent extends EventObject {
  type: typeof APPLY_EVENT;

  data?: {
    analytics: {
      campaignMedium: string | undefined;
      campaignSource: string | undefined;
      neuroIdIdentityId: string | undefined;
    };
  };
}

const apply = async (context: ApplyRequestMachineContext): Promise<ApplyResult> => {
  // TODO: [OR-1566] clean up after a/b test
  const oldDecisionCode = context.application?.lease?.decisionCode;
  if (oldDecisionCode === 'C2' && isLongForm(context.dealer)) {
    context.ldClient.track('c2-decline-resubmitted');
  }

  try {
    const optInSmsMarketing = context.selectedOptIntoSmsMarketing ?? !!localStorage.getItem(LocalStorageKeys.OptIn); // 'false' should NOT be stored in this localstorage key. Remove item if it should not be true
    const applyRequest: ApplyInput = {
      customer: {
        firstName: context.basicInfo?.firstName!,
        lastName: context.basicInfo?.lastName!,
        emailAddress: context.basicInfo?.emailAddress!,
        birthDate: context.basicInfoContinued?.birthDate!,
        cellPhone: context.cellPhone,
        accommodationAddress: nullifyLine2(context.accommodationAddress),
        billingAddress: nullifyLine2(context.billingAddress || context.accommodationAddress)!,
        shippingAddress: nullifyLine2(context.orderDetails?.customer?.shippingAddress) || undefined,
      },
      employment: {
        monthlyIncome: context.basicInfoContinued?.monthlyIncome!,
      },
      analytics: {
        ...buildAnalytics(),
        neuroIdIdentityId: context.shouldProcessNeuroId ? context.orderId : undefined,
        socureDeviceSessionId: context.socureDeviceSessionId ?? undefined,
      },
      orderId: context.orderId,
      optIntoSmsMarketing: optInSmsMarketing || null,
    };

    if (context.orderDetails?.items?.length) {
      applyRequest.cart = {
        items: context.orderDetails.items,
        shippingAmount: context.orderDetails.shippingAmnt,
        taxAmnt: context.orderDetails.taxAmnt,
      };
    }

    //we want to delete taxpayerIdentificationNumber when its an empty string so we dont get graphql validation errors. Maybe we should allow both
    //tokenized and untokenized to be sent to backend and we figure out which one to use there
    if (context.basicInfoContinued?.tokenizedTaxId) {
      applyRequest.customer.tokenizedTaxpayerIdentificationNumber = context.basicInfoContinued?.tokenizedTaxId;
    } else if (context?.customers?.length) {
      applyRequest.customer.tokenizedTaxpayerIdentificationNumber = context.customers[0].tokenizedTaxId;
    } else {
      applyRequest.customer.taxpayerIdentificationNumber = context.basicInfoContinued?.taxpayerIdentificationNumber;
    }

    const { data, errors } = await applicationFormService.apply(applyRequest);

    if (context.shouldProcessNeuroId) nid('applicationSubmit'); // messy place to put this, but its so much easier

    var result = {
      applyErrors: data?.apply?.applicationErrors,
      application: data?.apply?.application,
      applyResolverErrors: errors,
    } as ApplyResult;

    if (data?.apply?.applicationErrors?.length) {
      const cartValidationError = data.apply.applicationErrors.find((err) => err.__typename === 'CartValidation');
      if (cartValidationError) {
        result.cartValidationError = cartValidationError as CartValidation;
      }

      let newDecisionCode = '';
      const softDeclineError = data.apply.applicationErrors.find((err) => err.__typename === 'SoftDecline');
      if (softDeclineError) {
        newDecisionCode = softDeclineError?.extensions?.decisionCode ?? '';

        // For softDecline app retry to work, we need to get data from softDecline error
        result.softDeclineError = softDeclineError as SoftDecline;
        result.customerFlowType = CustomerFlowType.SingleCustomer;
      }

      const hardDeclineError = data.apply.applicationErrors.find((err) => err.__typename === 'HardDecline');
      if (hardDeclineError) {
        result.hardDeclineError = hardDeclineError as HardDecline;
      }

      // TODO: [OR-1566] clean up after a/b test
      if (oldDecisionCode === 'C2' && newDecisionCode !== 'C2' && isLongForm(context.dealer)) {
        context.ldClient.track('c2-decline-resolved');
      }
    } else {
      // TODO: [OR-1566] clean up after a/b test
      if (oldDecisionCode === 'C2' && isLongForm(context.dealer)) {
        context.ldClient.track('c2-decline-resolved');
      }

      result.customerFlowType = CustomerFlowType.VerifiedCustomer; // newCustomer becomes "verified" after approved submit
      localStorage.removeItem(LocalStorageKeys.OptIn); //removes sms marketing preference from local storage after submit goes through successfully

      // messy place to put this, but its so much easier
      if (context.shouldProcessNeuroId) nid('setRegisteredUserId', data?.apply?.application?.customer?.id?.toString());
      identify(data?.apply?.application?.customer?.id!);
    }
    return result;
  } catch (err: any) {
    var error = {} as NetworkError;
    switch (err?.networkError?.statusCode) {
      case 403:
        error.forbiddenError = createForbiddenErrorMessage(err);
        break;
      case 401:
        error.unauthorizedError = createUnauthorizedErrorMessage(err);
        break;
      default:
        error.errorGeneral = err;
    }
    throw error;
  }
};

export default apply;
